import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

/** Only field59/amazonaws CDN URLs need a processing probe. */
const isVideoProcessingUrl = (url: string): boolean =>
  url.includes('field59') || url.includes('amazonaws.com');

/** How often to re-probe while the video is still processing (ms). */
const POLL_INTERVAL_MS = 20_000;

/** Safety rail: stop processing checks after max. */
export const MAX_PROCESSING_CHECKS = 30;

/**
 * Probes a CDN URL and returns whether the video is still processing.
 * field59/amazonaws return 500 or 403 until transcoding is complete.
 */
const checkIsProcessing = async (url: string): Promise<boolean> => {
  try {
    const response = await fetch(url);
    return response.status === 500 || response.status === 403;
  } catch {
    return false;
  }
};

/**
 * Checks whether a video file URL is still processing, and keeps polling
 * until the video becomes ready.
 *
 * - Returns 'true' while a field59/amazonaws URL responds with 500/403.
 * - Polls every 20 s while processing; stops automatically when ready.
 * - Safety rail: after MAX_PROCESSING_CHECKS consecutive processing responses,
 *   polling stops and the hook returns false.
 * - Returns 'false' immediately for non-CDN URLs or when 'sourceUrl' is unset.
 * - No polling is started if the initial check shows the video is already ready.
 * - Concurrent callers with the same URL share one network request and one
 *   polling timer — React Query dedupes automatically by query key.
 */
export const useVideoProcessingCheck = (sourceUrl: string | undefined): boolean => {
  const [processingCheckCount, setProcessingCheckCount] = useState(0);

  useEffect(() => {
    setProcessingCheckCount(0);
  }, [sourceUrl]);

  const { data } = useQuery({
    queryKey: ['videoProcessing', sourceUrl],
    queryFn: async () => {
      const isProcessing = await checkIsProcessing(sourceUrl!);

      if (isProcessing) {
        setProcessingCheckCount((current) =>
          Math.min(current + 1, MAX_PROCESSING_CHECKS),
        );
      } else {
        setProcessingCheckCount(0);
      }

      return isProcessing;
    },
    enabled: !!sourceUrl && isVideoProcessingUrl(sourceUrl),
    refetchInterval: (query) =>
      query.state.data === true && processingCheckCount < MAX_PROCESSING_CHECKS
        ? POLL_INTERVAL_MS
        : false,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });

  if (data === true && processingCheckCount >= MAX_PROCESSING_CHECKS) {
    return false;
  }

  return data ?? false;
};
